import {ChangeDetectorRef, NgZone, OnDestroy, Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {interval, Subscription} from 'rxjs';
import {startWith} from 'rxjs/operators';

@Pipe({
  name: 'dateAgo',
  pure: false
})
export class DateAgoPipe implements PipeTransform, OnDestroy {

  private latestValue: any;
  private latestText: any;
  private timerSub: Subscription | null = null;
  private languageSub: Subscription;
  private nowTs = Date.now();

  public constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.languageSub = this.translateService.onLangChange.subscribe(() => {
      this.latestText = this.format(this.latestValue);
      this.cdr.markForCheck();
    });
  }

  transform(value: any, args?: any): any {
    if (!this.timerSub) {
      this.ngZone.runOutsideAngular(() => {
        this.timerSub = interval(60000).pipe(startWith(0)).subscribe(() => {
          this.nowTs = Date.now();
          this.latestText = this.format(this.latestValue);
          this.ngZone.run(() => this.cdr.markForCheck());
        });
      });
    }
    this.latestValue = value;
    const nextText = this.format(value);
    if (nextText !== this.latestText) {
      this.latestText = nextText;
    }
    return this.latestText;
  }

  ngOnDestroy(): void {
    this.languageSub.unsubscribe();
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = null;
    }
  }

  private format(value: any): any {
    if (value) {
      const seconds = Math.floor((this.nowTs - +new Date(value)) / 1000);
      if (seconds < 59) {
        return this.translateService.instant('pipe.date.just_now');
      }
      const intervals = {
        'year': 31536000,
        'month': 2592000,
        'week': 604800,
        'day': 86400,
        'hour': 3600,
        'minute': 60,
        'second': 1
      };
      let counter;
      for (const i in intervals) {
        counter = Math.floor(seconds / intervals[i]);
        if (counter > 0) {
          const unit = this.translateService.instant(this.getUnitKey(i, counter));
          return this.translateService.instant('pipe.date.ago', {count: counter, unit});
        }
      }
    }
    return value;
  }

  private getUnitKey(unit: string, count: number): string {
    if (count === 1) {
      return `pipe.date.${unit}`;
    }

    const language = this.translateService.currentLang || this.translateService.getDefaultLang();
    if (language === 'pl-PL') {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;
      const isFew = lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14);
      if (!isFew) {
        return `pipe.date.${unit}s_many`;
      }
    }

    return `pipe.date.${unit}s`;
  }

}
